import { Box, Button, Card, Checkbox, Divider, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, SelectChangeEvent, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { GlobalStateContext, IFilters } from '../wrappers/GlobalContext';
import { Close } from '@mui/icons-material';

type Props = {
  options: (string | number)[],
  labels?: (string | number)[],
  label: string,
  filterKey: string,
  overprice?: boolean,
  searchable?: boolean,
  disabled?: boolean,
  closeButton?: boolean,
}

function SelectMultiple({ options, label, filterKey, overprice = false, searchable, disabled, closeButton, labels }: Props) {
  const [search, setSearch] = useState<string>("");
  const { typedFilters, setTypedFilters, typedFiltersOverprice, setTypedFiltersOverprice } = useContext(GlobalStateContext);
  const { selectAll, setSelectAll } = useContext(GlobalStateContext);
  const [open, setOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string | number | true | string[]>) => {
    const { target: { value } } = event;
    if (overprice) {
      setTypedFiltersOverprice({
        ...typedFiltersOverprice,
        [filterKey]: typeof value === 'string' ? value.split(',') : value,
      });
    } else {
      setTypedFilters({
        ...typedFilters,
        [filterKey]: typeof value === 'string' ? value.split(',') : value,
      });
    }
  };

  useEffect(() => {
    const currentFilters = overprice ? typedFiltersOverprice : typedFilters;
    setSelectAll(options.length === (currentFilters[filterKey as keyof IFilters] || []).length);
  }, [options, overprice, typedFilters, typedFiltersOverprice]);

  const handleChangeAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      if (overprice) {
        setTypedFiltersOverprice({
          ...typedFiltersOverprice,
          [filterKey]: options,
        });
      } else {
        setTypedFilters({
          ...typedFilters,
          [filterKey]: options,
        });
      }
    } else {
      if (overprice) {
        setTypedFiltersOverprice({
          ...typedFiltersOverprice,
          [filterKey]: [],
        });
      } else {
        setTypedFilters({
          ...typedFilters,
          [filterKey]: [],
        });
      }
    }
    setSelectAll(event.target.checked);
  };

  const convertNumberToMonth = (number: number) => {
    const monthNames = [
      "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
      "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
    ];
    return number >= 1 && number <= 12 ? monthNames[number - 1] : "Mês Inválido";
  };

  const value = () => (overprice ? typedFiltersOverprice[filterKey as keyof IFilters] : typedFilters[filterKey as keyof IFilters]) || [];
  const isChecked = (option: string | number): boolean => Array.isArray(value()) ? (value() as (string | number)[]).includes(option) : false;
  const filteredOptions = () => search ? options.filter(opt => opt.toString().toLowerCase().includes(search.toLowerCase())) : options;

  return (
    <FormControl sx={{ width: '100%' }}>
      <InputLabel id={`${label}-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-label`}
        id={label}
        multiple
        input={<OutlinedInput label={label} />}
        value={value()}
        renderValue={(selected: any) => {
          if (filterKey === "month") {
            const monthNames = selected.map((monthNumber: number) => convertNumberToMonth(monthNumber));
            return monthNames.join(', ');
          } else {
            return selected.join(', ');
          }
        }}
        onChange={handleChange}
        disabled={disabled}
        open={open}
        onOpen={() => { setOpen(true) }}
        onClose={() => { setOpen(false) }}
      >
        {searchable && (
          <>
            <div className='p-2 flex'>
              <FormControlLabel control={<Checkbox checked={selectAll}
                onChange={handleChangeAll}
                inputProps={{ 'aria-label': 'controlled' }} />} label="Todos" sx={{ ml: 1 }} />
              <TextField
                className='w-full'
                variant='outlined'
                size='small'
                placeholder='Buscar...'
                value={search}
                type="search"
                onKeyDown={(e) => { e.stopPropagation(); }}
                onChange={(e) => setSearch(e.target.value)}
              />
              {closeButton && (
                <IconButton onClick={() => setOpen(false)} sx={{ ml: 8 }}>
                  <Close />
                </IconButton>
              )}
            </div>
            <Divider />
          </>
        )}

        {filteredOptions().map((option, index) => (
          <MenuItem key={option} value={option}>
            <Checkbox checked={isChecked(option)} />
            <ListItemText primary={labels ? labels[index] : (filterKey === "month" ? convertNumberToMonth(+option) : option)} />
          </MenuItem>
        ))}

        {closeButton && (
          <Box>
            <Button variant="contained" color="primary" sx={{ float: 'right', m: 2 }} onClick={() => setOpen(false)}>Ok</Button>
          </Box>
        )}
      </Select>
    </FormControl>
  );
}

export default SelectMultiple;
